import produce from 'immer';

import { IConfigurationInitialState, types } from './types';
import { restoreInitialState } from '../../utils/AppUtils';
import {
    readOperatingLocationBrowserStore,
    syncOperatingLocationBrowserStoreFromRedux,
    clearOperatingLocationBrowserStore,
} from '../../utils/operatingLocationBrowserStore';
import { types as messageType } from '../messages/types';

const initialState: IConfigurationInitialState = {
    fetchingConfig: false,
    configError: '',
    creatingConfig: false,
    updatingConfig: false,
    userSpecificConfiguration: undefined,
    globalConfiguration: undefined,
    fetchingGlobalConfig: false,
    fetchingUserLocation: false,
    userLocationErrMsgs: null,
    notification_token: '',
    guidedTourIndex: 0,
    influencerTiers: [],
    fetchingCampaignHostLtdAdmin: false,
    updatingCampaignHostLtdAdmin: false,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;

    switch (type) {
        case types.GET_CONFIGURATION:
            return produce(state, draftState => {
                draftState.fetchingConfig = true;
            });
        case types.GET_CONFIGURATION_SUCCESS:
            return produce(state, draftState => {
                draftState.userSpecificConfiguration = payload;
                draftState.fetchingConfig = false;
            });
        case types.GET_CONFIGURATION_FAILED:
            return produce(state, draftState => {
                draftState.fetchingConfig = false;
                draftState.configError = payload;
            });
        case types.CREATE_CONFIGURATION:
            return produce(state, draftState => {
                draftState.creatingConfig = true;
            });
        case types.CREATE_CONFIGURATION_SUCCESS:
            return produce(state, draftState => {
                draftState.userSpecificConfiguration = payload.data;
                draftState.creatingConfig = false;
            });
        case types.CREATE_CONFIGURATION_FAILED:
            return produce(state, draftState => {
                draftState.configError = payload;
            });
        case types.UPDATE_USER_CONFIG:
            return produce(state, draftState => {
                draftState.updatingConfig = true;
                // Optimistic merge so tour selectors flip before PATCH returns.
                const patch = payload?.payload;
                if (draftState.userSpecificConfiguration && patch && typeof patch === 'object') {
                    draftState.userSpecificConfiguration = {
                        ...draftState.userSpecificConfiguration,
                        ...patch,
                    };
                }
            });
        case types.UPDATE_USER_CONFIG_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingConfig = false;
                if (payload && typeof payload === 'object') {
                    draftState.userSpecificConfiguration = {
                        ...(draftState.userSpecificConfiguration || {}),
                        ...payload,
                    };
                }
            });
        case types.UPDATE_USER_CONFIG_FAILED:
            return produce(state, draftState => {
                draftState.updatingConfig = false;
                draftState.configError = payload;
            });
        case types.GET_GLOBAL_CONFIGURATION:
            return produce(state, draftState => {
                draftState.fetchingGlobalConfig = true;
                draftState.updatingConfig = false;
            });
        case types.GET_GLOBAL_CONFIGURATION_SUCCESS:
            return produce(state, draftState => {
                draftState.fetchingGlobalConfig = false;
                draftState.globalConfiguration = payload;
                draftState.influencerTiers = payload?.influencer_tiers ?? [];
            });
        case types.GET_GLOBAL_CONFIGURATION_FAIL:
            return produce(state, draftState => {
                draftState.fetchingGlobalConfig = false;
            });

        case types.UPDATE_GLOBAL_CONFIGURATION:
            return produce(state, draftState => {
                draftState.updatingConfig = true;
            });

        case types.UPDATE_GLOBAL_CONFIGURATION_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingConfig = false;
                if (draftState.globalConfiguration) {
                    draftState.globalConfiguration = {
                        ...draftState.globalConfiguration,
                        ...payload,
                    };
                }
                if (payload?.influencer_tiers) {
                    draftState.influencerTiers = payload.influencer_tiers;
                }
            });

        case types.UPDATE_GLOBAL_CONFIGURATION_FAIL:
            return produce(state, draftState => {
                draftState.updatingConfig = false;
                draftState.configError = payload;
            });

        case types.SHOW_PROJECT_GUIDE:
            return produce(state, draftState => {
                const { on } = payload;
                if (draftState.userSpecificConfiguration) draftState.userSpecificConfiguration.show_project_guide = on;
                draftState.showProjectGuideTutorial = on;
            });
        case types.GET_USER_LOCATION:
            return produce(state, draftState => {
                draftState.fetchingUserLocation = true;
            });
        case types.GET_USER_LOCATION_SUCCESS:
            return produce(state, draftState => {
                draftState.fetchingUserLocation = false;
            });
        case types.GET_USER_LOCATION_FAIL:
            return produce(state, draftState => {
                draftState.fetchingUserLocation = false;
                draftState.userLocationErrMsgs = payload;
            });
        case types.STORE_NOTIF_TOKEN:
            return produce(state, draftState => {
                draftState.notification_token = payload;
            });
        case types.SET_GUIDED_TOUR_INDEX:
            return produce(state, draftState => {
                draftState.guidedTourIndex = payload;
            });
        case types.SET_USER_LOCATION:
            return produce(state, draftState => {
                draftState.userCurrentLocation = payload;
            });

        case types.SET_OPERATING_LOCATION_OVERRIDE:
            return produce(state, draftState => {
                draftState.operatingLocationOverride = payload;
                syncOperatingLocationBrowserStoreFromRedux(
                    Boolean(draftState.operatingLocationPersistAcrossSessions),
                    payload,
                );
            });

        case types.CLEAR_OPERATING_LOCATION_OVERRIDE:
            return produce(state, draftState => {
                draftState.operatingLocationOverride = null;
                syncOperatingLocationBrowserStoreFromRedux(
                    Boolean(draftState.operatingLocationPersistAcrossSessions),
                    null,
                );
            });

        case types.SET_OPERATING_LOCATION_PERSIST_ACROSS_SESSIONS:
            return produce(state, draftState => {
                draftState.operatingLocationPersistAcrossSessions = payload;
                syncOperatingLocationBrowserStoreFromRedux(payload, draftState.operatingLocationOverride);
            });

        case types.HYDRATE_OPERATING_LOCATION_FROM_BROWSER: {
            const stored = readOperatingLocationBrowserStore();
            return produce(state, draftState => {
                if (stored.persistAcrossSessions && stored.override?.country_code) {
                    draftState.operatingLocationPersistAcrossSessions = true;
                    draftState.operatingLocationOverride = stored.override;
                }
            });
        }

        case messageType.UPDATE_UNREAD_COUNT:
            return produce(state, draftState => {
                const { unreadCount } = payload;
                if (draftState.userSpecificConfiguration) {
                    draftState.userSpecificConfiguration = {
                        ...draftState.userSpecificConfiguration,
                        unread_messages: unreadCount,
                    };
                }
            });
        case types.FETCH_CAMPAIGN_HOST_LTD_ADMIN:
            return produce(state, draftState => {
                draftState.fetchingCampaignHostLtdAdmin = true;
                draftState.campaignHostLtdAdminError = undefined;
            });
        case types.FETCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS:
            return produce(state, draftState => {
                draftState.fetchingCampaignHostLtdAdmin = false;
                draftState.campaignHostLtdAdmin = payload;
            });
        case types.FETCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL:
            return produce(state, draftState => {
                draftState.fetchingCampaignHostLtdAdmin = false;
                draftState.campaignHostLtdAdminError = payload;
            });
        case types.PATCH_CAMPAIGN_HOST_LTD_ADMIN:
            return produce(state, draftState => {
                draftState.updatingCampaignHostLtdAdmin = true;
                draftState.campaignHostLtdAdminError = undefined;
            });
        case types.PATCH_CAMPAIGN_HOST_LTD_ADMIN_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingCampaignHostLtdAdmin = false;
                draftState.campaignHostLtdAdmin = payload;
            });
        case types.PATCH_CAMPAIGN_HOST_LTD_ADMIN_FAIL:
            return produce(state, draftState => {
                draftState.updatingCampaignHostLtdAdmin = false;
                draftState.campaignHostLtdAdminError = payload;
            });

        case types.RESET_USER_CONFIG: {
            const stored = readOperatingLocationBrowserStore();
            const next = restoreInitialState(state, initialState);
            if (stored.persistAcrossSessions && stored.override?.country_code) {
                next.operatingLocationPersistAcrossSessions = true;
                next.operatingLocationOverride = stored.override;
            } else {
                clearOperatingLocationBrowserStore();
            }
            return next;
        }
        default:
            return state;
    }
};

export { reducer };
